import { isAxiosError } from 'axios';
import type { GeneratedRecordConflictCode, GeneratedRecordIssue } from './generated-record.types';

// A Record keyed by the union, not a list, so adding a code to the type without listing it here
// fails to compile instead of silently reading as "unknown code" at runtime.
const CONFLICT_CODES: Record<GeneratedRecordConflictCode, true> = {
  GENERATED_RECORD_IN_USE: true,
  GENERATED_RECORD_LOCK_MISMATCH: true,
  GENERATED_RECORD_STALE_RESPONSE: true,
  GENERATED_RECORD_MATRIX_MISMATCH: true,
  GENERATED_RECORD_SEQUENCE_CONFLICT: true,
  GENERATED_RECORD_OPEN_RETRY: true,
  GENERATED_RECORD_UPLOAD_HAS_PDF: true,
};

export const GENERATED_RECORD_TITLE_DUPLICATED = 'GENERATED_RECORD_TITLE_DUPLICATED';
const TITLE_DUPLICATED_MESSAGE = 'Já existe um Registro com este título.';
// The user-defined title travels outside the answers, so its error never names a field of the sheet.
const INDEXER_ERROR_KEY = 'indexer';

export interface GeneratedRecordTitleRefusal {
  message: string;
  indexerError: string | null;
  fieldIssues: GeneratedRecordIssue[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isConflictCode(value: unknown): value is GeneratedRecordConflictCode {
  return typeof value === 'string' && Object.hasOwn(CONFLICT_CODES, value);
}

function toCellRef(entry: unknown): GeneratedRecordIssue['cell'] {
  if (!isRecord(entry)) return undefined;
  const { tableId, rowIndex, columnIndex } = entry;
  if (typeof tableId !== 'string' || typeof rowIndex !== 'number') return undefined;
  return {
    tableId,
    rowIndex,
    ...(typeof columnIndex === 'number' ? { columnIndex } : {}),
  };
}

function toIssue(entry: unknown): GeneratedRecordIssue | null {
  if (!isRecord(entry)) return null;

  const { fieldId, code, message } = entry;
  if (typeof fieldId !== 'string' || typeof code !== 'string' || typeof message !== 'string') {
    return null;
  }

  const cell = toCellRef(entry.cell);
  return { fieldId, code, message, ...(cell === undefined ? {} : { cell }) };
}

function getResponseBody(error: unknown, status: number): Record<string, unknown> | null {
  if (!isAxiosError(error) || error.response?.status !== status) return null;

  const body: unknown = error.response.data;

  return isRecord(body) ? body : null;
}

export function getGeneratedRecordIssues(error: unknown): GeneratedRecordIssue[] | null {
  const body = getResponseBody(error, 422);
  if (body === null || !Array.isArray(body.issues)) return null;

  return body.issues.map(toIssue).filter((issue): issue is GeneratedRecordIssue => issue !== null);
}

export function getGeneratedRecordConflictCode(error: unknown): GeneratedRecordConflictCode | null {
  const body = getResponseBody(error, 409);
  if (body === null) return null;

  return isConflictCode(body.errorCode) ? body.errorCode : null;
}

export function getGeneratedRecordConflictMessage(error: unknown): string | null {
  const body = getResponseBody(error, 409);
  if (body === null) return null;

  return typeof body.message === 'string' && body.message.trim() !== '' ? body.message : null;
}

export function getGenerationRefusalMessage(error: unknown): string | null {
  const body = getResponseBody(error, 400);
  if (body === null) return null;

  return typeof body.message === 'string' && body.message.trim() !== '' ? body.message : null;
}

export const PENDING_ATTACHMENTS_UNAVAILABLE_MESSAGE =
  'Um ou mais anexos não estão mais disponíveis. Envie os arquivos novamente.';

// The server refuses this creation with a plain 400 and no error code, so its text is the only mark.
export function isPendingAttachmentsUnavailable(error: unknown): boolean {
  return getGenerationRefusalMessage(error) === PENDING_ATTACHMENTS_UNAVAILABLE_MESSAGE;
}

function firstMessage(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const message: unknown = value[0];
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

function titleFieldIssues(body: Record<string, unknown>): GeneratedRecordIssue[] {
  const errors = isRecord(body.errors) ? body.errors : {};
  const fromIssues = Array.isArray(body.issues)
    ? body.issues.map(toIssue).filter((issue): issue is GeneratedRecordIssue => issue !== null)
    : [];
  const issues = fromIssues.map((issue) => ({ ...issue, message: firstMessage(errors[issue.fieldId]) ?? issue.message }));
  const listed = new Set(issues.map((issue) => issue.fieldId));
  const fromErrors = Object.keys(errors).flatMap((fieldId) => {
    const message = firstMessage(errors[fieldId]);
    return listed.has(fieldId) || message === null ? [] : [{ fieldId, code: 'title', message }];
  });
  return [...issues, ...fromErrors].filter((issue) => issue.fieldId !== INDEXER_ERROR_KEY);
}

export function getGeneratedRecordTitleRefusal(error: unknown): GeneratedRecordTitleRefusal | null {
  const body = getResponseBody(error, 409);
  if (body?.errorCode !== GENERATED_RECORD_TITLE_DUPLICATED) return null;

  const errors = isRecord(body.errors) ? body.errors : {};
  const message = typeof body.message === 'string' && body.message.trim() !== '' ? body.message : TITLE_DUPLICATED_MESSAGE;
  return {
    message,
    indexerError: firstMessage(errors[INDEXER_ERROR_KEY]),
    fieldIssues: titleFieldIssues(body),
  };
}
